import { ChevronDown, CurrencyDollar, Edit01, InfoCircle } from '@untitled-ui/icons-react/build/cjs';
import parsePhoneNumberFromString from 'libphonenumber-js';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import CheckBox from 'src/components/CheckBox';
import InputText from 'src/components/InputText/InputText';
import InputTextArea from 'src/components/InputTextArea';
import NotFoundUI from 'src/components/NotFoundUI';
import SelectBox from 'src/components/SelectBox/SelectBox';
import Toggle from 'src/components/Toggle/Toggle';
import TooltipCmp from 'src/components/TooltipCmp';
import { CURRENCY, currencyOptions, QUOTE_CLASSIFICATION } from 'src/constants/common';
import useAddressDetails from 'src/hooks/useAddressDetails';
import { isViewQuoteData } from 'src/redux/Quoting.slice';
import { RootState } from 'src/redux/store';
import { getPricingRate } from 'src/services/CommonService';
import { logError } from 'src/services/LoggingService';
import { findServiceTypeIdBySlug, formatDecimalNumber } from 'src/utils/CommonFunctions';

import { IDangerousGoodsForm, IInBondForm } from '../Modals/quoteDetailModal.interface';
import { CLASSIFICATION_OPTIONS, initialQuoteDetailAction } from '../quoteDetail.const';

import AdditionalServiceModal from './AdditionalServiceModal';
import DangerousGoodsModal from './DangerousGoodsModal';
import InBondModal from './InBondModal';

const EDITABLE_SERVICES_SLUGS = ['in-bond', 'dangerous_goods'];

const FtlShipmentDetails = () => {
    const { t } = useTranslation();
    const isViewQuote = useSelector(isViewQuoteData);
    const { commodityOptions } = useSelector((data: RootState) => data.basic);
    const { vehicleTypeOptions, isVehicleTypeLoading, serviceTypes = { PICKUP: [], DELIVERY: [], OTHER: [] } } = useAddressDetails();

    const {
        control,
        watch,
        setValue,
        getValues,
        trigger,
        clearErrors,
        formState: { isSubmitted },
    } = useFormContext();

    const [isEquipmentExpanded, setIsEquipmentExpanded] = useState(false);
    const [filteredCommodityOptions, setFilteredCommodityOptions] = useState<any>([]);
    const [commoditySelectKey, setCommoditySelectKey] = useState<number>(0);
    const [commodityInputValue, setCommodityInputValue] = useState<string>('');
    const [isCommodityMenuOpen, setIsCommodityMenuOpen] = useState<boolean>(false);
    const [action, setAction] = useState(initialQuoteDetailAction);
    const [currencyRates, setCurrencyRates] = useState<{ cadRate: number; usdRate: number }>({ cadRate: 1, usdRate: 0.75 });
    const prevInsuranceCurrencyRef = useRef<string | null>(null);
    const isConvertingCurrencyRef = useRef<boolean>(false);

    const classification = watch('classification');
    const primaryEquipmentType = watch('primaryEquipmentType');
    const secondaryEquipmentTypes: string[] = watch('secondaryEquipmentTypes') || [];
    const selectedServices = watch('serviceId');
    const weightMeasurement = watch('weightMeasurement');
    const isLoadInsuranceEnabled = watch('isLoadInsuranceEnabled');
    const insuranceCommercialValue = watch('insuranceCommercialValue');
    const insuranceCurrencyType = watch('insuranceCurrencyType');

    const secondaryVehicleOptions = vehicleTypeOptions.filter((item: any) => item.value !== primaryEquipmentType);

    useEffect(() => {
        if (commodityOptions && commodityOptions.length > 0) {
            setFilteredCommodityOptions(commodityOptions);
        }
    }, [commodityOptions]);

    useEffect(() => {
        getPricingRate()
            .then((response: any) => {
                if (response?.data) {
                    setCurrencyRates({ cadRate: response.data.cadRate || 1, usdRate: response.data.usdRate || 0.75 });
                }
            })
            .catch(async (err: any) => {
                await logError(err, { errorMessage: err?.message || 'Error fetching pricing rates', errorLocation: 'FtlShipmentDetails: getPricingRate', storeInDatabase: true, logToConsole: true });
            });
    }, []);

    useEffect(() => {
        if (prevInsuranceCurrencyRef.current === null && insuranceCurrencyType) {
            prevInsuranceCurrencyRef.current = insuranceCurrencyType;
        }
    }, [insuranceCurrencyType]);

    const convertCurrency = useCallback(
        (value: number, fromCurrency: string, toCurrency: string): number => {
            if (!value || value === 0 || fromCurrency === toCurrency) return formatDecimalNumber(value || 0, 2);

            let convertedValue = 0;

            if (fromCurrency === CURRENCY.CAD && toCurrency === CURRENCY.USD) {
                convertedValue = value * (currencyRates.usdRate || 0.75);
            } else if (fromCurrency === CURRENCY.USD && toCurrency === CURRENCY.CAD) {
                convertedValue = value * (currencyRates.cadRate || 1);
            } else {
                convertedValue = value;
            }

            return formatDecimalNumber(convertedValue, 2);
        },
        [currencyRates]
    );

    useEffect(() => {
        if (prevInsuranceCurrencyRef.current && prevInsuranceCurrencyRef.current !== insuranceCurrencyType && insuranceCommercialValue && !isConvertingCurrencyRef.current) {
            isConvertingCurrencyRef.current = true;
            const numericValue = parseFloat(String(insuranceCommercialValue)) || 0;

            if (numericValue > 0) {
                setValue('insuranceCommercialValue', String(convertCurrency(numericValue, prevInsuranceCurrencyRef.current, insuranceCurrencyType)));

                if (isSubmitted) trigger('insuranceCommercialValue');
            }

            setTimeout(() => {
                isConvertingCurrencyRef.current = false;
            }, 100);
        }
        prevInsuranceCurrencyRef.current = insuranceCurrencyType;
    }, [insuranceCurrencyType, insuranceCommercialValue, convertCurrency, setValue, trigger, isSubmitted]);

    useEffect(() => {
        const numericValue = parseFloat(String(insuranceCommercialValue)) || 0;

        if (numericValue > 0 && insuranceCurrencyType) {
            const usdValue = insuranceCurrencyType === CURRENCY.USD ? numericValue : formatDecimalNumber(numericValue * (currencyRates.usdRate || 0.75), 2);
            setValue('insuranceCommercialValueUSD', String(usdValue));
        } else {
            setValue('insuranceCommercialValueUSD', '');
        }
    }, [insuranceCommercialValue, insuranceCurrencyType, currencyRates]);

    // ─── Action helpers ───────────────────────────────────────────────────────
    const handleAction =
        (type: string, isOpen: boolean = true) =>
        () => {
            setAction((old) => ({ ...old, [type]: isOpen }));
        };

    const handleClose = () => setAction(initialQuoteDetailAction);

    // ─── Select helpers ───────────────────────────────────────────────────────
    const handleSelectOption = (onChange: any) => (e: any) => onChange(e?.value);

    const handlePrimaryOption = (onChange: any) => (e: any) => {
        onChange(e?.label);
        setValue('primaryEquipmentType', e?.value);

        const currentSecondaryTypes = watch('secondaryEquipmentTypes') || [];
        const currentSecondaryFullNames = watch('secondaryEquipmentTypeFullNames') || [];

        if (currentSecondaryTypes.length > 0) {
            setValue(
                'secondaryEquipmentTypes',
                currentSecondaryTypes.filter((type: string) => type !== e?.value)
            );
            setValue(
                'secondaryEquipmentTypeFullNames',
                currentSecondaryFullNames.filter((name: string) => name !== e?.label)
            );
        }
    };

    const handleSecondaryEquipmentChange = (vehicleType: any) => (e: any) => {
        const currentTypes = secondaryEquipmentTypes || [];
        const currentNames = watch('secondaryEquipmentTypeFullNames') || [];

        if (e.target.checked) {
            setValue('secondaryEquipmentTypes', [...currentTypes, e.target.value]);
            setValue('secondaryEquipmentTypeFullNames', [...currentNames, vehicleType.label]);
        } else {
            setValue(
                'secondaryEquipmentTypes',
                currentTypes.filter((item: string) => item !== e.target.value)
            );
            setValue(
                'secondaryEquipmentTypeFullNames',
                currentNames.filter((item: string) => item !== vehicleType.label)
            );
        }
    };

    // ─── Load Insurance ───────────────────────────────────────────────────────
    const handleLoadInsuranceToggle = (onChange: any) => (e: any) => {
        onChange(e.target.checked);

        if (!e.target.checked) {
            setValue('insuranceCommercialValue', '');

            clearErrors(['insuranceCommercialValue', 'insuranceCommodity']);
        }

        if (isSubmitted) {
            trigger('insuranceCommercialValue');
            trigger('insuranceCommodity');
        }
    };

    const handleInsuranceCommercialValueChange = (onChange: any) => (e: any) => {
        onChange(e.target.value);
        if (isSubmitted) trigger('insuranceCommodity');
    };

    const handleCurrencyChange = (onChange: any) => (selectedOption: any) => onChange(selectedOption?.value || CURRENCY.CAD);

    // ─── Commodity (insuranceCommodity) ───────────────────────────────────────
    const handleCommodityMultipleChange = (onChange: any) => (selectedOptions: any) => {
        onChange(Array.isArray(selectedOptions) ? selectedOptions.map((o: any) => o.value) : []);
        setCommodityInputValue('');
        setFilteredCommodityOptions(commodityOptions);
        if (isSubmitted) trigger('insuranceCommercialValue');
    };

    const handleCommodityInputChange = (inputValue: string, actionMeta: any) => {
        if (actionMeta.action === 'input-change') {
            setCommodityInputValue(inputValue);
            setFilteredCommodityOptions(
                !inputValue.trim()
                    ? commodityOptions
                    : commodityOptions.filter((option: any) => {
                          const label = typeof option === 'string' ? option : option.label || '';
                          const value = typeof option === 'string' ? option : option.value || '';

                          return (label && label.toLowerCase().includes(inputValue.toLowerCase())) || (value && value.toLowerCase().includes(inputValue.toLowerCase()));
                      })
            );
        } else if (actionMeta.action === 'menu-close') {
            setCommodityInputValue('');
        }
    };

    const handleClearCommodities = (onChange: any) => () => {
        setIsCommodityMenuOpen(false);
        onChange([]);
        setCommodityInputValue('');
        setFilteredCommodityOptions(commodityOptions);
    };

    const handleCreateCommodityOption = (onChange: any) => (inputValue: string) => {
        const trimmedValue = inputValue?.trim();

        if (trimmedValue) {
            const currentValue = watch('insuranceCommodity') || [];
            onChange(Array.isArray(currentValue) ? [...currentValue, trimmedValue] : [trimmedValue]);
            setCommodityInputValue('');
            setFilteredCommodityOptions(commodityOptions);
            setCommoditySelectKey((prev) => prev + 1);
            if (isSubmitted) trigger('insuranceCommodity');
        }
    };

    const handleCommodityKeyDown = useCallback(
        (onChange: any, currentValue: string[]) => (event: React.KeyboardEvent) => {
            if (event.key === 'Backspace' && !commodityInputValue && Array.isArray(currentValue) && currentValue.length > 0) {
                event.preventDefault();
                onChange(currentValue.slice(0, -1));
                if (isSubmitted) trigger('insuranceCommodity');
            }
        },
        [commodityInputValue, isSubmitted, trigger]
    );

    // ─── Other Additional Services ────────────────────────────────────────────
    const handleEditService = (service: any) => () => {
        if (service?.slug === 'in-bond') handleAction('inBond')();
        else if (service?.slug === 'dangerous_goods') handleAction('dangerousGoods')();
    };

    const handleServiceChange = (e: any) => {
        let { value, checked, name } = e.target;
        value = parseInt(value);
        const currentServices = Array.isArray(selectedServices) ? selectedServices : [];

        if (checked) {
            if (name === 'in-bond') handleAction('inBond')();
            else if (name === 'dangerous_goods') handleAction('dangerousGoods')();
            else setValue('serviceId', [...currentServices, value]);
        } else {
            setValue(
                'serviceId',
                currentServices.filter((item: any) => item !== value)
            );

            if (name === 'in-bond') {
                setValue('inbond', false);
                setValue('inbondType', '');
                setValue('inbondName', '');
                setValue('inbondAddress', '');
                setValue('inbondContactMethod', '');
                setValue('inbondContactValue', '');
            } else if (name === 'dangerous_goods') {
                setValue('dangerousGoods', false);
                setValue('dangerGoodsType', '');
                setValue('unitedNationsNumber', '');
                setValue('packagingGroup', '');
                setValue('goodsClass', '');
                setValue('description', '');
                setValue('emergencyContactName', '');
                setValue('emergencyContactNumber', '');
                setValue('emergencyContactExt', '');
                setValue('emergencyEapnbr', '');
            }
        }
    };

    const getOtherServiceList = (data: any) => (
        <div className="flex flex-wrap -m-1">
            {data.map((val: any) =>
                val.slug !== 'load_insurance' && (classification === QUOTE_CLASSIFICATION.FTL || (classification !== QUOTE_CLASSIFICATION.FTL && val.loadLinkTerm !== null)) ? (
                    <div className="flex p-1 w-1/3" key={`service_${val.id}`}>
                        <CheckBox
                            id={`service_${val.id}`}
                            onChangeFunc={handleServiceChange}
                            name={val.slug}
                            value={val.id}
                            checked={selectedServices?.includes(val.id) || false}
                            parentClassName="mt-px"
                            disabled={isViewQuote}
                        />
                        <div className="leading-[0.9] space-x-1.5 flex items-center truncate pl-1.5 flex-1">
                            <label htmlFor={`service_${val.id}`} className={`leading-4 text-textSecondary text-xs font-medium truncate ${isViewQuote ? '!cursor-not-allowed' : 'cursor-pointer'}`}>
                                {val.name}
                            </label>
                            {selectedServices?.includes(val.id) && EDITABLE_SERVICES_SLUGS.includes(val.slug) && (
                                <TooltipCmp message={t('Common.Edit')} childrenClassName="!flex">
                                    <Edit01 className="ml-2 mt-px w-3.5 h-3.5 cursor-pointer flex-none hover:text-primary" onClick={handleEditService(val)} />
                                </TooltipCmp>
                            )}
                        </div>
                    </div>
                ) : null
            )}
        </div>
    );

    // ─── InBond / DangerousGoods modal default values ─────────────────────────
    const inbondContactValue = watch('inbondContactValue') || '';
    const parsedInbondPhone = inbondContactValue ? parsePhoneNumberFromString(inbondContactValue) : null;
    const inBondDefaultValues: IInBondForm = {
        inbondType: watch('inbondType') || '',
        inbondName: watch('inbondName') || '',
        inbondAddress: watch('inbondAddress') || '',
        inbondContactValue,
        inbondContactMethod: watch('inbondContactMethod') || 'email-address',
        countryCode: parsedInbondPhone?.country || 'CA',
    };

    const emergencyContactNumberValue = watch('emergencyContactNumber') || '';
    const parsedPhoneNumber = emergencyContactNumberValue ? parsePhoneNumberFromString(emergencyContactNumberValue) : null;
    const dangerousGoodsDefaultValues: IDangerousGoodsForm = {
        dangerGoodsType: watch('dangerGoodsType') || '',
        unitedNationsNumber: watch('unitedNationsNumber') || '',
        packagingGroup: watch('packagingGroup') || '',
        goodsClass: watch('goodsClass') || '',
        description: watch('description') || '',
        emergencyContactName: watch('emergencyContactName') || '',
        emergencyContactNumber: emergencyContactNumberValue,
        emergencyContactExt: watch('emergencyContactExt') || '',
        emergencyEapnbr: watch('emergencyEapnbr') || '1-888-CAN-UTEC',
        emergencyContactCountryCode: parsedPhoneNumber?.country || 'CA',
    };

    const handleInBond = (data: any) => {
        const inBondId = findServiceTypeIdBySlug((serviceTypes as any).OTHER || [], 'in-bond');
        const currentServiceId = getValues('serviceId') || [];
        setValue('serviceId', currentServiceId.includes(inBondId) ? currentServiceId : [...currentServiceId, inBondId]);
        setValue('inbond', true);
        setValue('inbondType', data.inbondType);
        setValue('inbondName', data.inbondName);
        setValue('inbondAddress', data.inbondAddress);
        setValue('inbondContactMethod', data.inbondContactMethod);
        setValue('inbondContactValue', data.inbondContactValue);
        handleClose();
    };

    const handleDangerousGoods = (data: IDangerousGoodsForm) => {
        const dangerousGoodsServiceId = findServiceTypeIdBySlug((serviceTypes as any).OTHER || [], 'dangerous_goods');
        const currentServiceId = getValues('serviceId') || [];
        setValue('serviceId', currentServiceId.includes(dangerousGoodsServiceId) ? currentServiceId : [...currentServiceId, dangerousGoodsServiceId]);
        setValue('dangerousGoods', true);
        setValue('dangerGoodsType', data.dangerGoodsType);
        setValue('unitedNationsNumber', data.unitedNationsNumber);
        setValue('packagingGroup', data.packagingGroup);
        setValue('goodsClass', data.goodsClass);
        setValue('description', data.description);
        setValue('emergencyContactName', data.emergencyContactName);
        setValue('emergencyContactNumber', data.emergencyContactNumber);
        setValue('emergencyContactExt', data.emergencyContactExt);
        setValue('emergencyEapnbr', data.emergencyEapnbr);
        handleClose();
    };

    return (
        <>
            <div className="space-y-3">
                <h6 className="text-grayLight900 text-xs font-medium">{t('Common.Load_Overview')}</h6>

                {/* Block 1: Service Type + Total Weight + Linear Footage */}
                <div className="p-3 rounded-lg border border-utilityGray200 bg-gray25">
                    <div className="flex gap-x-3">
                        <Controller
                            name="classification"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <SelectBox
                                    parentClassName="single-select-x-scroll flex-1"
                                    name="classification"
                                    required
                                    label={t('Common.Service_Type')}
                                    className="form_control"
                                    classComp="!bg-white"
                                    labelClassName="form_label mb-1.5 block"
                                    isDisabled={isViewQuote}
                                    options={CLASSIFICATION_OPTIONS}
                                    onChangeFunc={handleSelectOption(onChange)}
                                    value={CLASSIFICATION_OPTIONS.find((val: any) => value === val.value)}
                                />
                            )}
                        />
                        <Controller
                            name="totalWeight"
                            control={control}
                            render={({ field: { value, onChange }, fieldState: { error } }) => (
                                <InputText
                                    parentClassName="flex-1"
                                    inputWrapClass={isViewQuote ? 'transparent-disabled-input' : ''}
                                    label={t('Common.Total_Weight')}
                                    inputName="totalWeight"
                                    inputType="number"
                                    isAllowDecimal={true}
                                    maxLength={6}
                                    placeholder={t('QuotingHub.Common.Enter_total_weight')}
                                    labelClassName="mb-1.5 block"
                                    className={error?.message ? 'border border-red-500 border-solid' : ''}
                                    value={value}
                                    shouldFormatNumber={true}
                                    inputValueUnitType={weightMeasurement?.toLowerCase()}
                                    required
                                    onChangeFunc={onChange}
                                    isShowDisableClass={false}
                                    disabled={isViewQuote}
                                />
                            )}
                        />
                        <Controller
                            name="linearFootage"
                            control={control}
                            render={({ field: { value, onChange }, fieldState: { error } }) => (
                                <InputText
                                    parentClassName="flex-1"
                                    inputWrapClass={isViewQuote ? 'transparent-disabled-input' : ''}
                                    label={t('Common.Linear_Footage')}
                                    inputName="linearFootage"
                                    inputType="number"
                                    isAllowDecimal={true}
                                    placeholder={t('QuotingHub.Common.Enter_linear_footage')}
                                    labelClassName="mb-1.5 block"
                                    value={value}
                                    shouldFormatNumber={true}
                                    inputValueUnitType="ft"
                                    required
                                    errorText={error?.message || ''}
                                    isIconError={!!error?.message}
                                    isError={!!error?.message}
                                    onChangeFunc={onChange}
                                    isShowDisableClass={false}
                                    disabled={isViewQuote}
                                />
                            )}
                        />
                    </div>
                </div>

                {/* Block 2: Primary Equipment Type + Secondary Equipment Type */}
                <div className="p-3 rounded-lg border border-utilityGray200 bg-gray25 space-y-3">
                    {/* Row 3: Primary Equipment Type */}
                    <Controller
                        name="primaryEquipmentTypeFullName"
                        control={control}
                        render={({ field: { value, onChange }, fieldState: { error } }) => (
                            <SelectBox
                                parentClassName="flex-1 transparent-disabled-select"
                                classComp="!bg-white"
                                required
                                name="primaryEquipmentTypeFullName"
                                placeholder={t('QuotingHub.QuotingDashboard.Select_eqpt_type')}
                                label={t('QuotingHub.QuotingDashboard.Primary_Equipment_Type')}
                                labelClassName="form_label mb-1.5 block"
                                options={vehicleTypeOptions}
                                isDisabled={isViewQuote}
                                value={vehicleTypeOptions.find((item: any) => value === item.label)}
                                isLoading={isVehicleTypeLoading}
                                isMultipleSelection={false}
                                onChangeFunc={handlePrimaryOption(onChange)}
                                errorText={error?.message ? true : false}
                            />
                        )}
                    />

                    {/* Row 4: Secondary Equipment Type — checkbox grid */}
                    <div className="w-full space-y-1.5">
                        <label className="form_label mb-1.5 block">{t('QuotingHub.QuotingDashboard.Secondary_Equipment_Types')}</label>
                        <div className="rounded-lg border border-utilityGray200 bg-white p-1.5 flex flex-wrap relative">
                            <div className="absolute right-3 top-2.5">
                                <TooltipCmp message={isEquipmentExpanded ? t('Common.Collapse') : t('Common.Expand')} childrenClassName="!flex cursor-pointer">
                                    <div onClick={() => setIsEquipmentExpanded((prev) => !prev)}>
                                        <ChevronDown className={`w-4 h-4 text-grayLight600 transition-transform duration-300 ${isEquipmentExpanded ? 'rotate-180' : 'rotate-0'}`} />
                                    </div>
                                </TooltipCmp>
                            </div>
                            {/* First 3 always visible */}
                            {secondaryVehicleOptions.slice(0, 3).map((vehicleType: any, index: number) => (
                                <div className="w-1/3 p-1.5 flex items-center" key={`sec_eq_${index}`}>
                                    <CheckBox
                                        id={`sec_${vehicleType.value}`}
                                        name={vehicleType.label}
                                        checked={secondaryEquipmentTypes.includes(vehicleType.value)}
                                        parentClassName="!w-3.5 !h-3.5"
                                        value={vehicleType.value}
                                        onChangeFunc={handleSecondaryEquipmentChange(vehicleType)}
                                        disabled={isViewQuote}
                                    />
                                    <label htmlFor={`sec_${vehicleType.value}`} className={`text-textSecondary text-xs font-medium pl-1.5 ${isViewQuote ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                        {vehicleType.label}
                                    </label>
                                </div>
                            ))}
                            {/* Remaining — collapsible */}
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateRows: isEquipmentExpanded ? '1fr' : '0fr',
                                    transition: 'grid-template-rows 300ms ease',
                                    width: '100%',
                                }}
                            >
                                <div className="overflow-hidden flex flex-wrap">
                                    {secondaryVehicleOptions.slice(3).map((vehicleType: any, index: number) => (
                                        <div className="w-1/3 p-1.5 flex items-center" key={`sec_eq_extra_${index}`}>
                                            <CheckBox
                                                id={`sec_extra_${vehicleType.value}`}
                                                name={vehicleType.label}
                                                checked={secondaryEquipmentTypes.includes(vehicleType.value)}
                                                parentClassName="!w-3.5 !h-3.5"
                                                value={vehicleType.value}
                                                onChangeFunc={handleSecondaryEquipmentChange(vehicleType)}
                                                disabled={isViewQuote}
                                            />
                                            <label
                                                htmlFor={`sec_extra_${vehicleType.value}`}
                                                className={`text-textSecondary text-xs font-medium pl-1.5 ${isViewQuote ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                            >
                                                {vehicleType.label}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Block 3: Other Additional Services */}
                <div className="space-y-1.5">
                    <div className="flex items-center gap-x-1.5">
                        <h6 className="text-grayLight900 text-xs font-medium">{t('Common.Other_Additional_Services')}</h6>
                        <div className="cursor-pointer" onClick={handleAction('service')}>
                            <TooltipCmp message={t('Common.Other_Additional_Services')}>
                                <InfoCircle className="h-3.5 w-3.5 text-primary" />
                            </TooltipCmp>
                        </div>
                    </div>
                    <div className="rounded-lg border border-utilityGray200 bg-gray25 shadow-sm p-3">
                        {!isVehicleTypeLoading && (serviceTypes as any).OTHER?.length ? (
                            getOtherServiceList((serviceTypes as any).OTHER)
                        ) : isVehicleTypeLoading ? (
                            <div className="flex flex-wrap -m-1">
                                {Array.from({ length: 12 }).map((_, idx) => (
                                    <div className="flex p-1 w-1/3" key={`svc_loading_${idx}`}>
                                        <CheckBox id="svc_loading" onChangeFunc={() => {}} name="abc" value="xyz" checked={false} parentClassName="mt-0.5 custom-loading" />
                                        <div className="flex leading-4">
                                            <label className="md:ml-2 ml-1.5 text-sm leading-4 text-textSecondary font-medium custom-loading">Loading service</label>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <NotFoundUI
                                iconClassName="!hidden"
                                title={t('QuotingHub.QuotingDashboard.No_Services_Found')}
                                desc={t('QuotingHub.QuotingDashboard.No_other_Services_found_desc')}
                                containerClassName="!h-auto !my-5"
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Commodity */}
            <div className="space-y-1.5">
                <h6 className="text-grayLight900 text-xs font-medium">{t('Common.Commodity')}</h6>
                <Controller
                    name="insuranceCommodity"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => {
                        const selectedOptions =
                            (Array.isArray(value) ? value : [])
                                .map((val: string) => {
                                    const found = commodityOptions.find((o: any) => o.value === val);
                                    if (found) return found;
                                    if (val?.trim()) return { value: val, label: val };

                                    return null;
                                })
                                .filter(Boolean) || [];

                        const optionsToDisplay = filteredCommodityOptions.length > 0 || commodityInputValue.trim() ? filteredCommodityOptions : commodityOptions;

                        return (
                            <SelectBox
                                key={`commodity-select-${commoditySelectKey}`}
                                SelectDropdownWrapClass="multi-select-v2-dropdown"
                                parentClassName="w-full"
                                classComp="!bg-white"
                                options={optionsToDisplay}
                                value={selectedOptions}
                                onChangeFunc={handleCommodityMultipleChange(onChange)}
                                onInputChange={handleCommodityInputChange}
                                placeholder={t('Common.Select_commodity')}
                                errorText={error?.message ? true : false}
                                isClearable={true}
                                isLoading={isVehicleTypeLoading}
                                isMultipleSelection={true}
                                closeMenuOnSelect={false}
                                onClearFunc={handleClearCommodities(onChange)}
                                isMultipleEquipmentType={true}
                                maxVisibleValues={8}
                                keepMenuOpenOnMultiSelect={true}
                                autoCompleteSelect={true}
                                onCreateOption={handleCreateCommodityOption(onChange)}
                                isValidNewOption={Boolean(commodityInputValue && filteredCommodityOptions.length === 0)}
                                createOptionText={t('Common.Add_new_commodity')}
                                isSearchable={true}
                                forceSearchable={true}
                                noOptionMessage={t('Common.No_Options_Found')}
                                onMenuOpen={() => setIsCommodityMenuOpen(true)}
                                onMenuClose={() => {
                                    setIsCommodityMenuOpen(false);
                                    setCommodityInputValue('');
                                    setFilteredCommodityOptions(commodityOptions);
                                }}
                                menuIsOpen={isCommodityMenuOpen}
                                maintainFocusOnClear={true}
                                isShowInputValue={true}
                                defaultValueInput={commodityInputValue}
                                isDisabled={isViewQuote}
                                onKeyDown={handleCommodityKeyDown(onChange, value || [])}
                            />
                        );
                    }}
                />
            </div>

            {/* Load Insurance */}
            <div className="space-y-1.5">
                <div className="flex items-center gap-x-1">
                    <h6 className="text-grayLight900 text-xs font-medium">{t('QuotingHub.CreateQuote.Load_Insurance')}</h6>
                    <Controller
                        name="isLoadInsuranceEnabled"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <Toggle
                                isChecked={value || false}
                                labelTextClassName="!font-normal !text-xs"
                                onChange={handleLoadInsuranceToggle(onChange)}
                                switchClassName={isViewQuote ? 'cursor-not-allowed' : 'cursor-pointer'}
                                className={isViewQuote ? 'pointer-events-none' : ''}
                                name="isLoadInsuranceEnabled"
                                id="isLoadInsuranceEnabled"
                                isDisabled={isViewQuote}
                            />
                        )}
                    />
                </div>
                <div className="flex gap-x-2 items-center">
                    <Controller
                        name="insuranceCommercialValue"
                        control={control}
                        render={({ field: { value, onChange }, fieldState: { error } }) => (
                            <InputText
                                icon={<CurrencyDollar className="input-currency-sign" />}
                                className="pl-8 relative"
                                parentClassName="flex-1"
                                inputType="number"
                                value={value}
                                shouldFormatNumber={true}
                                onChangeFunc={handleInsuranceCommercialValueChange(onChange)}
                                placeholder={t('QuotingHub.Common.Enter_commercial_value')}
                                isError={!!error}
                                disabled={!isLoadInsuranceEnabled || isViewQuote}
                            />
                        )}
                    />
                    <Controller
                        name="insuranceCurrencyType"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <SelectBox
                                options={currencyOptions}
                                value={currencyOptions.find((o) => o.value === value)}
                                onChangeFunc={handleCurrencyChange(onChange)}
                                parentClassName="min-w-[80px]"
                                isDisabled={!isLoadInsuranceEnabled || isViewQuote}
                            />
                        )}
                    />
                </div>
            </div>

            {/* Special Instruction */}
            <div className="space-y-1.5">
                <h6 className="text-grayLight900 text-xs font-medium">Special Instruction</h6>
                <Controller
                    name="specialNotes"
                    control={control}
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                        <InputTextArea
                            value={value || ''}
                            onChangeFunc={onChange}
                            placeholder={t('QuotingHub.Common.Enter_Special_Instructions')}
                            errorText={error?.message}
                            maxLength={200}
                            disabled={isViewQuote}
                        />
                    )}
                />
            </div>

            {/* Modals */}
            {action.service && (
                <AdditionalServiceModal
                    handleClose={handleClose}
                    services={((serviceTypes as any).OTHER || []).filter(
                        (val: any) => val.slug !== 'load_insurance' && (classification === QUOTE_CLASSIFICATION.FTL || (classification !== QUOTE_CLASSIFICATION.FTL && val.loadLinkTerm !== null))
                    )}
                    title={t('Common.Other_Additional_Services')}
                />
            )}
            {action.inBond && <InBondModal handleClose={handleClose} handleInBond={handleInBond} defaultFormValues={inBondDefaultValues} />}
            {action.dangerousGoods && <DangerousGoodsModal handleClose={handleClose} handleDangerousGoods={handleDangerousGoods} defaultFormValues={dangerousGoodsDefaultValues} />}
        </>
    );
};

export default FtlShipmentDetails;
